import {
    useState,
    createContext,
    useMemo,
    useCallback,
    ReactNode,
} from "react";
import {
    AuthContextType,
    AuthType,
    AuthUser,
    RolesEnum,
    UserLoginData,
} from "../../types/Auth";
import { loginUser } from "../../services/auth/auth.service";
import { useLocalStorage } from "../../hooks";
import {
    dossierStorageKey,
    sessionStorageKey,
    settingStorageKey,
} from "../../consts";
import {
    getCurrentUser,
    getDossierStatus,
    getEstablishmentStatus,
    getSetting,
} from "../../services";
import { Session } from "../../types";
import { RoutesEnum } from "../../router/routes";
import { number } from "yup";

export const AuthContext = createContext<AuthContextType>({
    user: {
        id: null,
        firstName: "",
        lastName: "",
        roleId: null,
        establishment: null,
        phoneNumber: "",
        email: "",
        groups: [],
        region: null,
        establishmentGroup: null,
    },
    access: "",
    refresh: "",
    currentUserRoleId: -1,
    login: (user: AuthUser) => null,
    logout: () => null,
});

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
    const initialAuthState = {
        user: {
            id: null,
            firstName: "",
            lastName: "",
            roleId: null,
            establishment: null,
            phoneNumber: "",
            email: "",
            groups: [], // roles [admin,client,instructeur,synerpa,admin_reseau, admin_region]
            region: null,
            establishmentGroup: null,
        },
        access: "",
        refresh: "",
        currentUserRoleId: -1,
    };

    const [auth, setAuth] = useState<AuthType>(initialAuthState);
    const { setValue, deleteValue, storedValue } =
        useLocalStorage<Session>(sessionStorageKey);

    const login = useCallback(
        async (userLoginData: UserLoginData): Promise<AuthType | null> => {
            const authUser = await loginUser(userLoginData);
            if (authUser?.access) {
                await setValue({
                    ...storedValue,
                    auth: authUser,
                });
                const currentUserRes = await getCurrentUser();
                if (currentUserRes.status === 200) {
                    const user = currentUserRes.data;
                    let currentUserRoleId = user.groups[0];
                    if (user.groups.length > 1) {
                        const adminRoleId = user.groups.find(
                            (ug: number) => ug === RolesEnum.ADMIN
                        );
                        if (adminRoleId) {
                            currentUserRoleId = adminRoleId;
                        }
                    }

                    const auth = { ...authUser, user, currentUserRoleId };

                    setAuth(auth);
                    await setValue({
                        ...storedValue,
                        auth,
                    });
                    // storage dossier status
                    const dossierStatus = await getDossierStatus();
                    const establishmentStatus = await getEstablishmentStatus();
                    if (!dossierStatus.detail && !establishmentStatus.detail) {
                        const setting = await getSetting();
                        if (!setting.detail) {
                            await setValue({
                                ...storedValue,
                                auth,
                                [dossierStorageKey]: {
                                    dossierStatus,
                                    establishmentStatus,
                                },
                                [settingStorageKey]: setting,
                            });
                        }

                        return auth;
                    }
                }
            }
            return authUser;
        },
        []
    );

    const logout = useCallback(() => {
        deleteValue();
        //setAuth(initialAuthState);
        window.location.replace("/" + RoutesEnum.login);
    }, []);

    const AuthWrapperContext = useMemo(
        () => ({
            ...auth,
            login,
            logout,
        }),
        [auth, login, logout]
    );

    return (
        <AuthContext.Provider value={AuthWrapperContext}>
            {children}
        </AuthContext.Provider>
    );
};
